const baseUrl = import.meta.env.VITE_CONSOLE_API_HOST ?? "";

/**
 * A small extension of Error to carry HTTP status if desired.
 */
interface HttpError extends Error {
  status?: number;
}

async function request<T>(path: string, options: RequestInit = {}, jwt?: string): Promise<T> {
  // Build headers, always default to JSON
  const defaultHeaders: Record<string, string> = {
    "Content-Type": "application/json",
    ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
  };

  // Merge any headers passed in options.headers
  const mergedHeaders = {
    ...defaultHeaders,
    ...(options.headers as Record<string, string> | undefined),
  };

  const response = await fetch(`${baseUrl}${path}`, {
    ...options,
    headers: mergedHeaders,
  });

  if (!response.ok || response.status === 204) {
    const err = new Error(`HTTP ${response.status} ${response.statusText}`) as HttpError;
    err.status = response.status;
    throw err;
  }

  // If no content (e.g. DELETE returning 204), resolve to void
  if (response.status === 204) {
    // @ts-expect-error: T might be void
    return;
  }

  // Otherwise parse JSON as T
  return (await response.json()) as T;
}

export const get = <T>(path: string, jwt: string): Promise<T> => request<T>(path, { method: "GET" }, jwt);

export const post = <T, B = any>(path: string, body: B, jwt: string): Promise<T> =>
  request<T>(
    path,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
    jwt
  );

export const put = <T, B = any>(path: string, body: B, jwt: string): Promise<T> =>
  request<T>(
    path,
    {
      method: "PUT",
      body: JSON.stringify(body),
    },
    jwt
  );

export const del = (path: string, jwt: string): Promise<void> => request<void>(path, { method: "DELETE" }, jwt);
